import * as R from 'ramda';
import { useDispatch } from 'react-redux';
import React, {
  memo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
// components
import ImageCropper from '../image-cropper';
import { openModal, closeModal } from '../modal/actions';
// helpers
import * as G from '../../helpers';
// icons
import * as I from '../../svgs';
// ui
import { Box, Iframe, AbsoluteBox, RelativeBox } from '../../ui';
// component document-preview
import { ImageUI, CloseIconCircle, ImageViewerWrapper } from './ui';
//////////////////////////////////////////////////

const whiteColor = G.getTheme('colors.white');
const iconColor = G.getTheme('colors.dark.blue');

const ImagePreview = memo(({
  src,
  handleClose,
  updateImage,
  isEditEnabled,
}) => {
  const dispatch = useDispatch();

  const [scale, setScale] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const lastMousePosition = useRef({ x: 0, y: 0 });

  const handleWheel = useCallback((event) => {
    event.preventDefault();

    const delta = event.deltaY > 0 ? -0.1 : 0.1;

    setScale((prevScale) => Math.min(Math.max(prevScale + delta, 0.5), 5));
  }, []);

  const handleMouseDown = useCallback((event) => {
    setIsPanning(true);

    if (event.button === 0) {
      lastMousePosition.current = { x: event.clientX, y: event.clientY };
    }

    if (event.button === 1) {
      setScale(1);
      setIsPanning(false);
      setPosition({ x: 0, y: 0 });
    }
  }, [lastMousePosition]);

  const handleMouseMove = useCallback((event) => {
    if (!isPanning) return;

    const dx = event.clientX - lastMousePosition.current.x;
    const dy = event.clientY - lastMousePosition.current.y;

    lastMousePosition.current = { x: event.clientX, y: event.clientY };

    if (R.and(imageRef.current, containerRef.current)) {
      const container = containerRef.current.getBoundingClientRect();
      const image = imageRef.current.getBoundingClientRect();

      const containerWidth = container.width;
      const containerHeight = container.height;

      const scaledImageWidth = image.width * scale;
      const scaledImageHeight = image.height * scale;

      const minX = Math.min(0, (containerWidth - scaledImageWidth) / 2);
      const maxX = Math.max(0, (scaledImageWidth - containerWidth) / 2);

      const minY = Math.min(0, (containerHeight - scaledImageHeight) / 2);
      const maxY = Math.max(0, (scaledImageHeight - containerHeight) / 2);

      setPosition((prevPosition) => ({
        x: Math.min(Math.max(prevPosition.x + dx, -maxX), -minX),
        y: Math.min(Math.max(prevPosition.y + dy, -maxY), -minY),
      }));
    }
  }, [imageRef, isPanning, containerRef, lastMousePosition]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  const enterFullscreen = useCallback((event) => {
    event.preventDefault();

    const element = imageRef.current;

    if (element) {
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
    }
  }, [imageRef]);

  const handleUpdateImage = useCallback((file) => {
    dispatch(closeModal());

    updateImage(file);
  }, [updateImage]);

  const handleCropOrRotateImage = useCallback(() => {
    const modal = {
      p: '15px',
      component: (
        <ImageCropper src={src} submitAction={handleUpdateImage} />
      ),
      options: {
        title: G.getWindowLocale('titles:change-photo', 'Change Photo'),
      },
    };

    dispatch(openModal(modal));
  }, [src, handleUpdateImage]);

  return (
    <ImageViewerWrapper
      ref={containerRef}
      isPanning={isPanning}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={(event) => handleWheel(event)}
      onMouseDown={(event) => handleMouseDown(event)}
      onMouseMove={(event) => handleMouseMove(event)}
    >
      <ImageUI
        src={src}
        alt='Preview'
        scale={scale}
        ref={imageRef}
        position={position}
      />
      <AbsoluteBox top={10} right={10}>
        {isEditEnabled && (
          <Box
            cursor='pointer'
            onClick={handleCropOrRotateImage}
            title={G.getWindowLocale('titles:edit', 'Edit')}
          >
            {I.pencil(iconColor, 32, 32)}
          </Box>
        )}
        <Box
          ml={16}
          cursor='pointer'
          onClick={enterFullscreen}
          title={G.getWindowLocale('titles:fullscreen', 'Enter fullscreen')}
        >
          {I.fullScreen(iconColor, 32, 32)}
        </Box>
        {G.isFunction(handleClose) && (
          <Box
            ml={16}
            cursor='pointer'
            onClick={handleClose}
            title={G.getWindowLocale('titles:close', 'Close')}
          >
            {I.closeIcon(iconColor, 32, 32)}
          </Box>
        )}
      </AbsoluteBox>
    </ImageViewerWrapper>
  );
});

const getIconPropsByZoomLevel = (zoomLevel) => {
  // Interpolation here gives the wrong result
  const zoomData = {
    0.5: [24, 20, 27, 250],
    0.67: [19, 13, 21, 185],
    0.75: [18, 13, 17, 165],
    0.8: [17, 12, 16, 154],
    0.9: [14, 11, 15, 138],
    1: [13, 10, 14, 127],
    1.1: [12, 9, 13, 114],
    1.25: [10.5, 7.5, 11, 102],
    1.5: [9, 6, 11, 85],
    1.75: [7.5, 5, 9.5, 72],
    2: [7, 4.5, 8.5, 64],
  };

  const [size, p, top, right] = R.or(R.prop(R.clamp(0.5, 2, zoomLevel), zoomData), [7, 4.5, 8.5, 64]);

  return { p, top, size, right };
};

const PDFPreview = ({ src, type, handleClose }) => {
  const iframeRef = useRef();

  const [zoomLevel, setZoomLevel] = useState(1);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  useEffect(() => {
    const iframe = iframeRef.current;

    const handleLoad = () => setIframeLoaded(true);

    let observer;

    if (iframe) {
      iframe.addEventListener('load', handleLoad);

      // eslint-disable-next-line no-undef
      observer = new ResizeObserver(() => setZoomLevel(G.getCurrentZoomLevel()));

      observer.observe(iframe);
    }

    return () => {
      if (iframe) {
        iframe.removeEventListener('load', handleLoad);

        observer?.unobserve(iframe);
      }
    };
  }, []);

  const iconProps = getIconPropsByZoomLevel(zoomLevel);

  return (
    <RelativeBox width='100%' height='100%'>
      <Iframe type={type} src={src} ref={iframeRef} />
      {R.and(iframeLoaded, G.isFunction(handleClose)) && (
        <CloseIconCircle
          {...R.omit(['size'], iconProps)}
          onClick={handleClose}
          bg={G.getTheme('colors.dark.grey')}
          title={G.getWindowLocale('titles:close', 'Close')}
        >
          {I.closeIcon(whiteColor, iconProps.size, iconProps.size)}
        </CloseIconCircle>
      )}
    </RelativeBox>
  );
};

const Preview = ({ doc, handleClose, updateImage, isEditEnabled }) => {
  const src = R.path(['preview'], doc);
  const type = R.path(['file', 'type'], doc);

  if (type.match(/image.*/)) {
    return (
      <ImagePreview
        src={src}
        handleClose={handleClose}
        updateImage={updateImage}
        isEditEnabled={isEditEnabled}
      />);
  }

  return <PDFPreview src={src} type={type} handleClose={handleClose} />;
};

export default Preview;
